import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, MapPin, Trophy, Users } from 'lucide-react';
import { VenueGame, venueGamesService } from '@/services/venueGamesService';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { ScorekeeperApplicationModal } from '@/components/games/ScorekeeperApplicationModal';

interface VenueUpcomingGamesProps {
  venueId: string;
}

export const VenueUpcomingGames = ({ venueId }: VenueUpcomingGamesProps) => {
  const [games, setGames] = useState<VenueGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState<VenueGame | null>(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const user = supabaseUser || internalUser;

  useEffect(() => {
    loadGames();
  }, [venueId]);

  const loadGames = async () => {
    if (!venueId) {
      console.log('VenueUpcomingGames: No venue ID provided');
      return;
    }
    
    try {
      const upcomingGames = await venueGamesService.getUpcomingGames(venueId);
      // Filter to only show approved games
      const approvedGames = upcomingGames.filter(game => 
        game.status === 'approved' && game.venue_approved === true
      );
      setGames(approvedGames);
    } catch (error) {
      console.error('Error loading games:', error);
      // Only show error toast if there's a venue ID but loading failed
      if (venueId) {
        console.log('VenueUpcomingGames: Unable to load upcoming games for venue:', venueId);
        // Don't show toast error for better user experience
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApplyForScorekeeper = (game: VenueGame) => {
    if (!user?.id) {
      toast.error('Please log in to apply for scorekeeper position');
      return;
    }

    setSelectedGame(game);
    setShowApplicationModal(true);
  };

  const handleApplicationSubmitted = () => {
    setShowApplicationModal(false);
    setSelectedGame(null);
    loadGames(); // Refresh the list to show updated status
  };

  const formatGameTime = (date: string, time: string) => {
    try {
      const gameDateTime = new Date(`${date}T${time}`);
      return {
        date: format(gameDateTime, 'MMM dd, yyyy'),
        time: format(gameDateTime, 'h:mm a') + ' CST'
      };
    } catch {
      return { date: date, time: time };
    }
  };

  const getPostedByText = (game: VenueGame) => {
    const role = game.posted_by_role;
    const name = game.posted_by_profile?.first_name || game.posted_by_profile?.username || 'Unknown';
    
    if (role === 'venue') return `Posted by Venue`;
    if (role === 'coach') return `Posted by Coach ${name}`;
    if (role === 'team-owner') return `Posted by Team Owner ${name}`;
    return `Posted by ${name}`;
  };

  const getTeamName = (team: any): string => {
    if (!team) return 'TBD';
    if (Array.isArray(team)) return team[0]?.team_name || 'TBD';
    return team.team_name || 'TBD';
  };

  const getGameMatchup = (game: VenueGame): string => {
    const homeTeam = getTeamName(game.home_team);
    const awayTeam = getTeamName(game.away_team);
    return `${homeTeam} vs ${awayTeam}`;
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-gray-900 to-black border-gray-800">
        <CardContent className="p-6">
          <div className="text-center text-gray-400">Loading upcoming games...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-gray-900 to-black border-gray-800">
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-white flex items-center gap-2">
          <Trophy className="w-6 h-6 text-primary" />
          Upcoming Games
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        {games.length === 0 ? (
          <div className="text-center text-gray-400 py-8">
            No upcoming games scheduled
          </div>
        ) : (
          <div className="space-y-4">
            {games.map((game) => {
              const { date, time } = formatGameTime(game.game_date, game.game_time);
              return (
                <div key={game.id} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-white">{getGameMatchup(game)}</h3>
                        <Badge variant="outline" className="bg-blue-900/20 text-blue-300 border-blue-300/30 text-xs">
                          #{game.game_code}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-400">{getPostedByText(game)} • Points: {game.scorekeeper_points || 50}</p>
                      {game.game_title !== getGameMatchup(game) && (
                        <p className="text-xs text-gray-500">{game.game_title}</p>
                      )}
                      {game.sport?.sport_name && (
                        <p className="text-xs text-primary font-medium">Sport: {game.sport.sport_name}</p>
                      )}
                    </div>
                    {getTeamName(game.team) !== 'TBD' && (
                      <Badge variant="secondary" className="bg-primary/20 text-primary">
                        {getTeamName(game.team)}
                      </Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="flex items-center gap-2 text-gray-300">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm">{date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">{time}</span>
                    </div>
                    {game.field_location && (
                      <div className="flex items-center gap-2 text-gray-300">
                        <MapPin className="w-4 h-4" />
                        <span className="text-sm">{game.field_location}</span>
                      </div>
                    )}
                  </div>

                  {game.game_description && (
                    <p className="text-gray-300 text-sm mb-4">{game.game_description}</p>
                  )}

                  {game.scorekeeper_needed && !game.scorekeeper_assigned && (
                    <div className="bg-primary/10 border border-primary/30 rounded-lg p-3 mb-3">
                      <div className="flex items-center justify-between">
                      <div>
                        <p className="text-primary font-medium text-sm">Scorekeeper Needed (1 Primary + 3 Backups)</p>
                        <p className="text-gray-300 text-xs">
                          Earn {game.scorekeeper_points || 50} player points • Automatic backup system
                        </p>
                      </div>
                        <Button 
                          onClick={() => handleApplyForScorekeeper(game)}
                          size="sm"
                          className="bg-sports-green text-black hover:bg-sports-green/90 font-bold"
                        >
                          Apply Now
                        </Button>
                      </div>
                    </div>
                  )}

                  {game.scorekeeper_assigned && (
                    <div className="flex items-center gap-2 text-green-400 text-sm">
                      <Users className="w-4 h-4" />
                      <span>Scorekeeper assigned (with backup coverage)</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
      
      {/* Scorekeeper Application Modal */}
      <ScorekeeperApplicationModal
        game={selectedGame as any}
        isOpen={showApplicationModal}
        onClose={() => {
          setShowApplicationModal(false);
          setSelectedGame(null);
        }}
        onApplicationSubmitted={handleApplicationSubmitted}
      />
    </Card>
  );
};